/** The storm marks on the map: MapLibre's own GeoJSON layers over one
 * source, no shader of ours. Static geometry (the lines, the forecast
 * points, the ensemble members) is rebuilt when the set of storms or the
 * toggles change; the time-dependent part — the solid past of each
 * forecast, the current position, the wind radii at it and the name — is
 * rebuilt on every playhead move, from a few dozen points, so it stays
 * cheap enough to run at playback rate. */

import type { Feature, FeatureCollection, Geometry } from "geojson";
import type {
  ExpressionSpecification,
  FilterSpecification,
  GeoJSONSource,
  Map as MaplibreMap,
} from "maplibre-gl";

import {
  agencyCode,
  agencyColor,
  NEUTRAL_DARK_GROUND,
  NEUTRAL_LIGHT_GROUND,
  TC_BEST_AGENCY,
} from "./agencies";
import { RADII_COLORS, sshsClass } from "./intensity";
import { localStormName } from "./names";
import {
  isTcEnsemble,
  type TcPoint,
  type TcRadii,
  type TcStorm,
} from "./schema";
import {
  ensembleLines,
  forecastLine,
  modelTracks,
  pointTime,
  positionAt,
  radiiRing,
  unwrapLongitudes,
  type LonLat,
  type TrackPosition,
} from "./tracks";

const TC_SOURCE = "tc-tracks";
const LAYERS = {
  radii: "tc-radii",
  radiiLine: "tc-radii-line",
  bestRadii: "tc-best-radii",
  member: "tc-member",
  modelSolid: "tc-model-solid",
  modelDotted: "tc-model-dotted",
  best: "tc-best",
  intensityCasing: "tc-intensity-casing",
  intensity: "tc-intensity",
  agencyFuture: "tc-agency-future",
  agencyPast: "tc-agency-past",
  bestPoint: "tc-best-point",
  point: "tc-point",
  current: "tc-current",
  lineLabel: "tc-line-label",
  modelLabel: "tc-model-label",
  nameLabel: "tc-name-label",
} as const;

/** The layers a click on the map may hit a storm point through, nearest
 * on top first. */
export const TC_CLICKABLE_LAYERS: readonly string[] = [
  LAYERS.current,
  LAYERS.point,
  LAYERS.bestPoint,
];

/** What a clicked point carries, as the feature's `data` property (a JSON
 * string: MapLibre flattens nested properties). */
export interface TcPointData {
  storm: string;
  name: string;
  /** `forecast` for an agency's forecast point, `best` for a best-track
   * point, `current` for the interpolated position at the playhead. */
  source: "forecast" | "best" | "current";
  /** The agency key, or the best track's key. */
  agency: string;
  code: string;
  time: string;
  /** Seconds from the forecast's base; absent on a best track. */
  lead?: number;
  number?: string;
  lat: number;
  lon: number;
  vmax: number | null;
  pmin: number | null;
  radii: TcRadii | null;
  class: string | null;
  rmw: number | null;
  gust: number | null;
}

export function pointDataOf(feature: {
  properties?: unknown;
}): TcPointData | null {
  const properties = feature.properties as
    Record<string, unknown> | null | undefined;
  const raw = properties?.data;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw) as TcPointData;
  } catch {
    return null;
  }
}

/** What to draw of one storm. */
export interface StormView {
  storm: TcStorm;
  /** Agency forecasts to draw, by key. */
  agencies: ReadonlySet<string>;
  /** Deterministic model tracks to draw, by key (an ensemble's mean
   * counts under its own key). */
  models: ReadonlySet<string>;
  /** Draw ensemble members (the spaghetti) for the models drawn. */
  members: boolean;
  /** Draw the best tracks. */
  best: boolean;
  /** Draw the best track's wind radii: filled at the current time, and as
   * outlines at every synoptic point before it — the area its winds swept.
   * Off when absent (the live viewer). */
  bestRadii?: boolean;
  /** Draw the US best track thick and colored by Saffir–Simpson class,
   * its points filled with it (`intensity.ts`). Off when absent. */
  bestIntensity?: boolean;
}

interface Properties {
  kind: string;
  /** A best-track point's fill: its intensity class's color. */
  fill?: string;
  color: string;
  storm: string;
  code?: string;
  label?: string;
  threshold?: string;
  dotted?: boolean;
  opacity?: number;
  data?: string;
}

function pointData(
  storm: TcStorm,
  source: TcPointData["source"],
  agency: string,
  code: string,
  point: TcPoint,
  extra: { lead?: number; number?: string } = {},
): string {
  const data: TcPointData = {
    storm: storm.id,
    name: localStormName(storm.name, storm.basin) ?? storm.id,
    source,
    agency,
    code,
    time: point.time,
    lat: point.lat,
    lon: point.lon,
    vmax: point.vmax,
    pmin: point.pmin,
    radii: point.radii,
    class: point.class,
    rmw: point.rmw,
    gust: point.gust,
    ...extra,
  };
  return JSON.stringify(data);
}

/** The interpolated position as a point, for the current marker's card. */
function positionPoint(time: number, position: TrackPosition): TcPoint {
  return {
    time: new Date(time).toISOString().replace(/\.\d{3}Z$/, "Z"),
    lat: position.lat,
    lon: position.lon,
    vmax: position.vmax,
    pmin: position.pmin,
    radii: position.radii,
    class: position.class,
    rmw: null,
    gust: null,
    cone: null,
  };
}

function feature(geometry: Geometry, properties: Properties): Feature {
  return { type: "Feature", geometry, properties };
}

function line(coordinates: LonLat[], properties: Properties): Feature | null {
  return coordinates.length > 1
    ? feature({ type: "LineString", coordinates }, properties)
    : null;
}

function empty(): FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}

/** The outline features of a best-track point's radii, by point: they
 * depend on nothing but the point, and a slider drag asks for the same
 * few dozen again on every step. */
const PAST_RINGS = new WeakMap<TcPoint, Feature[]>();

/** The past radii's outlines: the 34-kt ring carries the swept area, the
 * inner two only hint at it. */
const BEST_RADII_OPACITY: Record<string, number> = { "34": 0.45, "50": 0.3, "64": 0.25 };

const RADII_OPACITY: Record<string, number> = {
  "34": 0.22,
  "50": 0.3,
  "64": 0.4,
};

/** The best track drawn by intensity: the US one (one-minute winds, the
 * Saffir–Simpson scale's own), else the first with a wind. */
function intensityKey(storm: TcStorm): string | null {
  const keys = Object.keys(storm.best).sort((a, b) => (a === "usa" ? -1 : b === "usa" ? 1 : a.localeCompare(b)));
  return keys.find((key) => storm.best[key]!.points.some((point) => point.vmax !== null)) ?? null;
}

/** That track as a casing and one segment per step, each in the class of
 * the point it starts from. */
function intensityFeatures(storm: TcStorm, key: string): Feature[] {
  const points = storm.best[key]!.points;
  const features: Feature[] = [];
  const casing = line(forecastLine(points), { kind: "intensity-casing", color: "", storm: storm.id });
  if (casing) features.push(casing);
  for (let index = 0; index + 1 < points.length; index += 1) {
    const from = points[index]!;
    const segment = line(forecastLine([from, points[index + 1]!]), {
      kind: "intensity",
      color: sshsClass(from.vmax)?.color ?? "#999999",
      storm: storm.id,
    });
    if (segment) features.push(segment);
  }
  return features;
}

export class StormLayers {
  private views: StormView[] = [];
  private time: number | null = null;
  private neutral = NEUTRAL_LIGHT_GROUND;
  private halo = "#ffffff";
  private staticFeatures: Feature[] = [];
  private added = false;

  constructor(private readonly map: MaplibreMap) {}

  /** Add the source and layers once the style is loaded; `before` is the
   * layer they go under (the basemap's first symbol layer, so a city keeps
   * its name over a track's). */
  ensure(before?: string): boolean {
    if (this.added) return true;
    const map = this.map;
    if (map.getSource(TC_SOURCE)) {
      this.added = true;
      return true;
    }
    map.addSource(TC_SOURCE, { type: "geojson", data: empty(), buffer: 64 });
    const kind = (value: string): ExpressionSpecification => [
      "==",
      ["get", "kind"],
      value,
    ];
    const both = (
      a: ExpressionSpecification,
      b: ExpressionSpecification,
    ): FilterSpecification => ["all", a, b];
    const width = (base: number): ExpressionSpecification => [
      "interpolate",
      ["linear"],
      ["zoom"],
      2,
      base * 0.7,
      6,
      base,
      10,
      base * 1.6,
    ];
    map.addLayer(
      {
        id: LAYERS.bestRadii,
        type: "line",
        source: TC_SOURCE,
        filter: kind("best-radii"),
        paint: {
          "line-color": ["get", "color"],
          "line-width": 0.8,
          "line-opacity": ["get", "opacity"],
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.radii,
        type: "fill",
        source: TC_SOURCE,
        filter: kind("radii"),
        paint: {
          "fill-color": ["get", "color"],
          "fill-opacity": ["get", "opacity"],
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.radiiLine,
        type: "line",
        source: TC_SOURCE,
        filter: kind("radii"),
        paint: {
          "line-color": ["get", "color"],
          "line-width": 1.2,
          "line-opacity": 0.85,
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.member,
        type: "line",
        source: TC_SOURCE,
        filter: kind("member"),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": width(0.7),
          "line-opacity": 0.35,
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.modelSolid,
        type: "line",
        source: TC_SOURCE,
        filter: both(kind("model"), ["!", ["to-boolean", ["get", "dotted"]]]),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": width(1.4),
          "line-opacity": 0.85,
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.modelDotted,
        type: "line",
        source: TC_SOURCE,
        filter: both(kind("model"), ["to-boolean", ["get", "dotted"]]),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": width(1.4),
          "line-opacity": 0.85,
          "line-dasharray": [0.1, 2],
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.best,
        type: "line",
        source: TC_SOURCE,
        filter: kind("best"),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": width(1.6),
          "line-opacity": 0.7,
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.intensityCasing,
        type: "line",
        source: TC_SOURCE,
        filter: kind("intensity-casing"),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": this.halo, "line-width": width(5.4), "line-opacity": 0.9 },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.intensity,
        type: "line",
        source: TC_SOURCE,
        filter: kind("intensity"),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": width(3.4) },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.bestPoint,
        type: "circle",
        source: TC_SOURCE,
        filter: kind("bestpoint"),
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            1.4,
            6,
            2.2,
            10,
            3.5,
          ],
          "circle-color": ["coalesce", ["get", "fill"], this.halo],
          "circle-stroke-color": ["get", "color"],
          "circle-stroke-width": 1,
          "circle-opacity": 0.9,
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.agencyFuture,
        type: "line",
        source: TC_SOURCE,
        filter: kind("agency"),
        layout: { "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": width(2.2),
          "line-dasharray": [2, 1.6],
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.agencyPast,
        type: "line",
        source: TC_SOURCE,
        filter: kind("agency-past"),
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": width(2.2) },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.point,
        type: "circle",
        source: TC_SOURCE,
        filter: kind("point"),
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            1.8,
            6,
            2.6,
            10,
            4,
          ],
          "circle-color": ["get", "color"],
          "circle-stroke-color": this.halo,
          "circle-stroke-width": 0.8,
        },
      },
      before,
    );
    map.addLayer(
      {
        id: LAYERS.current,
        type: "circle",
        source: TC_SOURCE,
        filter: kind("current"),
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            2,
            4,
            6,
            5.5,
            10,
            8,
          ],
          "circle-color": ["get", "color"],
          "circle-stroke-color": this.halo,
          "circle-stroke-width": 1.8,
        },
      },
      before,
    );
    // The labels sit on top of everything, the basemap's names included:
    // a track's source is what the line means, and a town under it must
    // not silence it. Along-line codes appear once the line is long enough
    // on screen to carry them and grow with the zoom.
    const labelSize: ExpressionSpecification = [
      "interpolate",
      ["linear"],
      ["zoom"],
      3,
      9,
      6,
      11,
      9,
      13,
    ];
    map.addLayer({
      id: LAYERS.lineLabel,
      type: "symbol",
      source: TC_SOURCE,
      filter: kind("agency"),
      minzoom: 2.5,
      layout: {
        "symbol-placement": "line",
        "symbol-spacing": 220,
        "text-field": ["get", "code"],
        "text-font": ["Noto Sans Medium"],
        "text-size": labelSize,
        "text-letter-spacing": 0.1,
        "text-max-angle": 35,
        "text-padding": 2,
        "text-keep-upright": true,
        "text-rotation-alignment": "map",
        "text-pitch-alignment": "viewport",
      },
      paint: {
        "text-color": ["get", "color"],
        "text-halo-color": this.halo,
        "text-halo-width": 1.6,
      },
    });
    // The model tracks and the best tracks carry their code too, a step
    // later in the zoom: they are the quieter lines.
    map.addLayer({
      id: LAYERS.modelLabel,
      type: "symbol",
      source: TC_SOURCE,
      filter: ["any", kind("model"), kind("best")],
      minzoom: 4,
      layout: {
        "symbol-placement": "line",
        "symbol-spacing": 260,
        "text-field": ["get", "code"],
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 4, 9, 9, 11],
        "text-letter-spacing": 0.08,
        "text-max-angle": 35,
        "text-padding": 2,
        "text-keep-upright": true,
        "text-rotation-alignment": "map",
        "text-pitch-alignment": "viewport",
      },
      paint: {
        "text-color": ["get", "color"],
        "text-halo-color": this.halo,
        "text-halo-width": 1.4,
        "text-opacity": 0.85,
      },
    });
    map.addLayer({
      id: LAYERS.nameLabel,
      type: "symbol",
      source: TC_SOURCE,
      filter: kind("current"),
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["Noto Sans Medium"],
        "text-size": 12,
        "text-anchor": "left",
        "text-offset": [0.9, 0],
        "text-padding": 4,
        "text-optional": true,
      },
      paint: {
        "text-color": ["get", "color"],
        "text-halo-color": this.halo,
        "text-halo-width": 1.8,
      },
    });
    this.added = true;
    this.applyInk();
    this.publish();
    return true;
  }

  /** The neutral ink and the halo follow the ground the map is on; the
   * agency colors never do. */
  setInk(darkGround: boolean): void {
    const neutral = darkGround ? NEUTRAL_DARK_GROUND : NEUTRAL_LIGHT_GROUND;
    if (neutral === this.neutral && this.added) return;
    this.neutral = neutral;
    this.halo = darkGround
      ? "rgba(12, 16, 24, 0.9)"
      : "rgba(255, 255, 255, 0.92)";
    this.rebuildStatic();
    this.applyInk();
    this.publish();
  }

  private applyInk(): void {
    if (!this.added) return;
    for (const id of [LAYERS.point, LAYERS.current])
      this.map.setPaintProperty(id, "circle-stroke-color", this.halo);
    this.map.setPaintProperty(LAYERS.bestPoint, "circle-color", ["coalesce", ["get", "fill"], this.halo]);
    this.map.setPaintProperty(LAYERS.intensityCasing, "line-color", this.halo);
    for (const id of [LAYERS.lineLabel, LAYERS.modelLabel, LAYERS.nameLabel])
      this.map.setPaintProperty(id, "text-halo-color", this.halo);
  }

  setViews(views: StormView[]): void {
    this.views = views;
    this.rebuildStatic();
    this.publish();
  }

  setTime(time: number | null): void {
    this.time = time;
    this.publish();
  }

  /** The views and the time at once, published once (`setViews` then
   * `setTime` would send the source twice). */
  update(views: StormView[], time: number | null): void {
    this.views = views;
    this.time = time;
    this.rebuildStatic();
    this.publish();
  }

  clear(): void {
    this.views = [];
    this.staticFeatures = [];
    this.publish();
  }

  private colorOf(agency: string): string {
    return agencyColor(agency) ?? this.neutral;
  }

  private rebuildStatic(): void {
    const features: Feature[] = [];
    for (const view of this.views) {
      const storm = view.storm;
      if (view.best) {
        const graded = view.bestIntensity ? intensityKey(storm) : null;
        if (graded !== null) features.push(...intensityFeatures(storm, graded));
        for (const [key, track] of Object.entries(storm.best)) {
          const agency = TC_BEST_AGENCY[key] ?? null;
          const color = agency ? this.colorOf(agency) : this.neutral;
          const code = key.toUpperCase();
          const drawn = line(forecastLine(track.points), {
            kind: "best",
            color,
            storm: storm.id,
            code: `BEST · ${code}`,
          });
          if (drawn) features.push(drawn);
          for (const point of track.points) {
            features.push(
              feature(
                { type: "Point", coordinates: [point.lon, point.lat] },
                {
                  kind: "bestpoint",
                  color: key === graded ? this.neutral : color,
                  fill: key === graded ? sshsClass(point.vmax)?.color : undefined,
                  storm: storm.id,
                  code,
                  data: pointData(storm, "best", key, code, point),
                },
              ),
            );
          }
        }
      }
      for (const [model, value] of Object.entries(storm.models)) {
        if (!view.models.has(model)) continue;
        if (view.members && isTcEnsemble(value)) {
          for (const { member, line: coordinates } of ensembleLines(value)) {
            const drawn = line(coordinates, {
              kind: "member",
              color: this.neutral,
              storm: storm.id,
              code: `${model}:${member}`,
            });
            if (drawn) features.push(drawn);
          }
        }
      }
      for (const { model, forecast, fromEnsemble } of modelTracks(storm)) {
        if (!view.models.has(model)) continue;
        const drawn = line(forecastLine(forecast.points), {
          kind: "model",
          color: this.neutral,
          storm: storm.id,
          code: agencyCode(model),
          dotted: fromEnsemble || model !== "gfs",
        });
        if (drawn) features.push(drawn);
      }
      for (const [agency, forecast] of Object.entries(storm.agencies)) {
        if (!view.agencies.has(agency)) continue;
        const color = this.colorOf(agency);
        const code = agencyCode(agency);
        const drawn = line(forecastLine(forecast.points), {
          kind: "agency",
          color,
          storm: storm.id,
          code,
        });
        if (drawn) features.push(drawn);
        for (const point of forecast.points) {
          features.push(
            feature(
              { type: "Point", coordinates: [point.lon, point.lat] },
              {
                kind: "point",
                color,
                storm: storm.id,
                code,
                data: pointData(storm, "forecast", agency, code, point, {
                  lead: point.lead,
                  number: forecast.number,
                }),
              },
            ),
          );
        }
      }
    }
    this.staticFeatures = features;
  }

  /** The time-dependent features for one storm at the current time. */
  private dynamicFeatures(view: StormView): Feature[] {
    const time = this.time;
    if (time === null) return [];
    const storm = view.storm;
    const features: Feature[] = [];
    let named = false;
    const name = localStormName(storm.name, storm.basin) ?? storm.id;
    for (const [agency, forecast] of Object.entries(storm.agencies)) {
      if (!view.agencies.has(agency)) continue;
      const points = forecast.points;
      if (points.length === 0) continue;
      const color = this.colorOf(agency);
      const code = agencyCode(agency);
      const last = pointTime(points[points.length - 1]!);
      if (time > last) {
        const drawn = line(forecastLine(points), {
          kind: "agency-past",
          color,
          storm: storm.id,
          code,
        });
        if (drawn) features.push(drawn);
        continue;
      }
      const position = positionAt(points, time);
      if (position === null) continue;
      const past = unwrapLongitudes([
        ...points
          .slice(0, position.index + 1)
          .map((point): LonLat => [point.lon, point.lat]),
        [position.lon, position.lat],
      ]);
      const drawn = line(past, {
        kind: "agency-past",
        color,
        storm: storm.id,
        code,
      });
      if (drawn) features.push(drawn);
      if (position.radii) {
        for (const threshold of ["34", "50", "64"] as const) {
          const quadrants = position.radii[threshold];
          if (!quadrants) continue;
          const ring = radiiRing(position.lat, position.lon, quadrants);
          if (!ring) continue;
          features.push(
            feature(
              { type: "Polygon", coordinates: [ring] },
              {
                kind: "radii",
                color,
                storm: storm.id,
                threshold,
                opacity: RADII_OPACITY[threshold],
              },
            ),
          );
        }
      }
      const label = named
        ? ""
        : position.class
          ? `${name} · ${position.class}`
          : name;
      named = true;
      features.push(
        feature(
          { type: "Point", coordinates: [position.lon, position.lat] },
          {
            kind: "current",
            color,
            storm: storm.id,
            code,
            label,
            data: pointData(
              storm,
              "current",
              agency,
              code,
              positionPoint(time, position),
              {
                lead: Math.round((time - Date.parse(forecast.base)) / 1000),
                number: forecast.number,
              },
            ),
          },
        ),
      );
    }
    if (view.best && view.bestRadii) features.push(...this.bestRadiiFeatures(storm, time));
    if (!named && view.best) {
      // Before any forecast starts the playhead is in the observed past:
      // the marker rides the best track instead.
      for (const [key, track] of Object.entries(storm.best)) {
        const position = positionAt(track.points, time);
        if (position === null) continue;
        const agency = TC_BEST_AGENCY[key] ?? null;
        const color = agency ? this.colorOf(agency) : this.neutral;
        const label = position.class ? `${name} · ${position.class}` : name;
        const code = key.toUpperCase();
        features.push(
          feature(
            { type: "Point", coordinates: [position.lon, position.lat] },
            {
              kind: "current",
              color,
              storm: storm.id,
              code,
              label,
              data: pointData(
                storm,
                "current",
                key,
                code,
                positionPoint(time, position),
              ),
            },
          ),
        );
        break;
      }
    }
    return features;
  }

  /** The wind radii of the first best track that reports them (the US
   * one carries quadrants; the RSMCs' ellipses are not kept), each
   * threshold in its own color (`intensity.ts`): outlines at each synoptic
   * point up to `time`, and the radii at `time` itself filled. */
  private bestRadiiFeatures(storm: TcStorm, time: number): Feature[] {
    const keys = Object.keys(storm.best).sort((a, b) => (a === "usa" ? -1 : b === "usa" ? 1 : a.localeCompare(b)));
    const key = keys.find((item) => storm.best[item]!.points.some((point) => point.radii));
    if (key === undefined) return [];
    const features: Feature[] = [];
    const rings = (lat: number, lon: number, radii: TcRadii, kind: string, opacity: Record<string, number>) => {
      for (const threshold of ["34", "50", "64"] as const) {
        const quadrants = radii[threshold];
        if (!quadrants) continue;
        const ring = radiiRing(lat, lon, quadrants);
        if (!ring) continue;
        features.push(
          feature(
            { type: "Polygon", coordinates: [ring] },
            { kind, color: RADII_COLORS[threshold], storm: storm.id, threshold, opacity: opacity[threshold] },
          ),
        );
      }
    };
    for (const point of storm.best[key]!.points) {
      if (pointTime(point) > time) break;
      const hour = new Date(pointTime(point)).getUTCHours();
      if (!point.radii || hour % 6 !== 0) continue;
      // A past point's rings never change: made once, kept with the point.
      let cached = PAST_RINGS.get(point);
      if (!cached) {
        const before = features.length;
        rings(point.lat, point.lon, point.radii, "best-radii", BEST_RADII_OPACITY);
        cached = features.slice(before);
        PAST_RINGS.set(point, cached);
      } else features.push(...cached);
    }
    const position = positionAt(storm.best[key]!.points, time);
    if (position?.radii) rings(position.lat, position.lon, position.radii, "radii", RADII_OPACITY);
    return features;
  }

  private publish(): void {
    if (!this.added) return;
    const source = this.map.getSource(TC_SOURCE) as GeoJSONSource | undefined;
    if (!source) return;
    const features = [...this.staticFeatures];
    for (const view of this.views) features.push(...this.dynamicFeatures(view));
    source.setData({ type: "FeatureCollection", features });
  }
}
